import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
  OnDestroy,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router } from "@angular/router";
import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
// Translate
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// App services
import { SessionDataService } from "../../../../services/shared/SessionDataService.service";
import { ManagementControllerService } from "../../../../services/server/ManagementController.service";
import { DeviceRouteSelectorService } from "../../../../services/shared/DeviceRouteSelectorService.service";
import { NavigationHelperService } from "../../../../services/shared/NavigationHelperService.service";
// Interfaces
import {
  GlobalSearchCups,
  GlobalSearchDeviceMini
} from "../../../../screens/dashboard/management/ManagementInterface.type";
import { PROFILES } from "../../../../../assets/profiles/profiles";
import { Agrupation } from "../../../../interfaces/AgrupationGlobalInterface.type";
// Variables
import { ScrollingModule } from "@angular/cdk/scrolling";
import { Client } from "../../../../interfaces/ClientGlobalInterface.type";
import { Entity } from "../../../../interfaces/EntityGlobalInterface.type";

/**
 * Top bar search box for devices or CUPS: queries management search and lists matches in a virtual-scroll dropdown; CUPS search is hidden in data protection.
 * `meterList`/`cupsList` show a loading state until loaded; emits `goToSelectionFlag` with the chosen result.
 */
@Component({
  selector: "app-navbar-top-search-box",
  templateUrl: "./navbar-top-search-box.component.html",
  styleUrls: ["./navbar-top-search-box.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, FormsModule, ScrollingModule, TranslateModule]
})
export class NavbarTopSearchBoxComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  cupsSelected: boolean = false;
  searchOptionSelected: string = this.translate.instant("device");
  componentInitiated: boolean = false;
  searchActive: boolean;
  entitySub: any;
  loading: boolean = true;

  @Input() sessionProfile: string;
  @Input() currentCupsName: string;
  @Input() type: string;
  @Input()
  get meterList(): any[] {
    return this._meterList;
  }
  set meterList(meterList: any[]) {
    this._meterList = meterList;
    if (!this.cupsSelected && !this._meterList) {
      this.loading = true;
      this.searchOptionSelected = this.translate.instant("loading");
    } else {
      if (!this.cupsSelected && this._meterList) {
        this.loading = false;
        this.searchOptionSelected = this.translate.instant("device");
      }
    }
    if (!this.cupsSelected && this.componentInitiated) {
      this.setSearchDropdownHeight();
    }
  }
  _meterList: any[];
  @Input()
  get cupsList(): any[] {
    return this._cupsList;
  }
  set cupsList(cupsList: any[]) {
    this._cupsList = cupsList;
    if (this.cupsSelected && !this._cupsList) {
      this.loading = true;
      this.searchOptionSelected = this.translate.instant("loading");
    } else {
      if (this.cupsSelected && this._cupsList) {
        this.loading = false;
        this.searchOptionSelected = this.currentCupsName;
      }
    }
    if (this.cupsSelected && this.componentInitiated) {
      this.setSearchDropdownHeight();
    }
  }
  _cupsList: any[];
  cupsMatches: GlobalSearchCups[];
  deviceMatches: GlobalSearchDeviceMini[];

  @Output() dropdownFilterFlag = new EventEmitter<any>();
  @Output() goToSelectionFlag = new EventEmitter<any>();
  @Output() resetSearchBoxFlag = new EventEmitter<any>();
  @Output() updateSearchActiveFlag = new EventEmitter<string>();

  @ViewChild("searchDropdown") searchDropdown: ElementRef;
  @ViewChild("searchFilter") searchFilter: ElementRef;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly DeviceRouteSelectorService: DeviceRouteSelectorService,
    private readonly ManagementController: ManagementControllerService,
    private readonly NavigationHelperService: NavigationHelperService,
    private readonly SessionDataService: SessionDataService,
    private readonly translate: TranslateService,
    private readonly router: Router
  ) {
    effect(() => {
      const entity = this.SessionDataService.entity();
      if (entity !== null) {
        this.cupsSelected = false;
        this.searchActive = false;
        this.searchOptionSelected = this.translate.instant("device");
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.componentInitiated = true;
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Filter flag
  dropdownFilter(filterValue: any, type: string, cupsSelected: boolean): void {
    this.deviceMatches = null;
    this.cupsMatches = null;
    this.dropdownFilterFlag.emit({
      filterValue: filterValue?.trim(),
      type: type,
      cupsSelected: cupsSelected
    });
  }

  // Go-to-selection flag
  goToSelection(cupsSelected: boolean, selection: any): void {
    this.goToSelectionFlag.emit({
      cupsSelected: cupsSelected,
      selection: selection
    });
  }

  // Search box reset flag
  resetSearchBox(type: string): void {
    this.cupsMatches = null;
    this.deviceMatches = null;
    this.resetSearchBoxText();
    this.resetSearchBoxFlag.emit(type);
    this.searchActive = false;
  }

  // Reset search box text
  resetSearchBoxText(): void {
    if (this.searchFilter) {
      this.searchFilter.nativeElement.value = "";
    }
  }

  // Active search flag
  updateSearchActive(type: string): void {
    this.updateSearchActiveFlag.emit(type);
  }

  // Set search box height
  setSearchDropdownHeight(): void {
    let searchList: any[] = this.cupsSelected ? this.cupsList : this.meterList;
    if (searchList?.length < 10) {
      this.searchDropdown.nativeElement.style.setProperty("--search-height", searchList.length);
    } else {
      this.searchDropdown.nativeElement.style.setProperty("--search-height", 10);
    }
  }

  // Check device on enter in the search box
  checkDeviceToGo(selected: string): void {
    // Device selection
    if (!this.cupsSelected) {
      let deviceChecked = this._meterList.find((device: any) => device.descripcion === selected);
      if (deviceChecked) {
        this.goToSelectionFlag.emit({
          cupsSelected: this.cupsSelected,
          selection: deviceChecked
        });
        // Global search if ARSON
      } else {
        this.searchForDevice(selected);
      }
      // CUPS selection
    } else {
      let cupsChecked = this._cupsList.find((cups: any) => cups.descripcion === selected);
      if (cupsChecked) {
        this.goToSelectionFlag.emit({
          cupsSelected: this.cupsSelected,
          selection: cupsChecked
        });
      } else {
        this.searchForCups(selected);
      }
    }
  }

  searchForDevice(selected: string): void {
    this.ManagementController.searchDevice({
      nroSerie: selected,
      devEui: null,
      rfModule: null
    }).subscribe(response => {
      if (response["code"] === 0 && response["body"]?.length > 0) {
        const deviceMatches: GlobalSearchDeviceMini[] = response["body"];
        if (deviceMatches.length === 1) {
          this.goToDevice(deviceMatches[0]);
        } else {
          this.deviceMatches = deviceMatches;
        }
      }
    });
  }

  goToDevice(device: GlobalSearchDeviceMini): void {
    const navigation = this.checkDeviceNavigation(device);
    if (navigation?.agrupation) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      this.SessionDataService.sendAgrupation(navigation.agrupation);
    } else if (navigation?.entity) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      this.SessionDataService.sendEntity(navigation.entity);
    } else if (navigation?.client) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      this.SessionDataService.sendClient(navigation.client);
    }

    this.deviceMatches = null;
    this.DeviceRouteSelectorService.getDeviceRoute(device.metrologyType, device.id);
  }

  searchForCups(selected: string): void {
    this.ManagementController.searchCups({
      clave: selected
    }).subscribe(response => {
      if (response["code"] === 0 && response["body"]?.length > 0) {
        const cupsMatches: GlobalSearchCups[] = response["body"];
        if (cupsMatches.length === 1) {
          this.goToCups(cupsMatches[0]);
        } else {
          cupsMatches.forEach(cups => {
            if (cups.entity) {
              if (
                this.sessionProfile === PROFILES.ARSON ||
                this.sessionProfile === PROFILES.USUARIO_ARSON
              ) {
                const navigation = this.NavigationHelperService.findEntityInAllClients(
                  "id",
                  cups.entity
                );
                cups.entityDesc = navigation?.entity?.entity;
              } else {
                const entity = this.NavigationHelperService.findEntityInCurrentClient(
                  "id",
                  cups.entity
                );
                cups.entityDesc = entity?.entity;
              }
            }
          });
          this.cupsMatches = cupsMatches;
        }
      }
    });
  }

  goToCups(cups: GlobalSearchCups): void {
    if (cups.entity != untracked(() => this.SessionDataService.entity()?.id)) {
      const navigation = this.checkCupsNavigation(cups);
      if (navigation?.entity) {
        this.SessionDataService.sendAvoidNavigationReload(true);
        this.SessionDataService.sendEntity(navigation.entity);
      }
    }
    this.cupsMatches = null;
    this.router.navigate(["/cups/detalle/", cups.id]);
  }

  checkDeviceNavigation(device: GlobalSearchDeviceMini): {
    client?: Client;
    entity?: Entity;
    agrupation?: Agrupation;
  } {
    if (device.agrupationId) {
      return this.sessionProfile === PROFILES.ARSON ||
        this.sessionProfile === PROFILES.USUARIO_ARSON
        ? this.NavigationHelperService.findAgrupationInAllClients("id", device.agrupationId)
        : this.NavigationHelperService.findAgrupationInAllEntities("id", device.agrupationId);
    } else if (device.entityId) {
      return this.sessionProfile === PROFILES.ARSON ||
        this.sessionProfile === PROFILES.USUARIO_ARSON
        ? this.NavigationHelperService.findEntityInAllClients("id", device.entityId)
        : {
            entity: this.NavigationHelperService.findEntityInCurrentClient("id", device.entityId)
          };
    } else if (device.clientId) {
      if (
        this.sessionProfile === PROFILES.ARSON ||
        this.sessionProfile === PROFILES.USUARIO_ARSON
      ) {
        const client = untracked(() => this.SessionDataService.clientList()).find(
          (client: Client) => client.clientId === device.clientId
        );
        return { client: client };
      }
    }
    return null;
  }

  checkCupsNavigation(cups: GlobalSearchCups): {
    client?: Client;
    entity?: Entity;
    agrupation?: Agrupation;
  } {
    if (cups.entity) {
      return this.sessionProfile === PROFILES.ARSON ||
        this.sessionProfile === PROFILES.USUARIO_ARSON
        ? this.NavigationHelperService.findEntityInAllClients("id", cups.entity)
        : {
            entity: this.NavigationHelperService.findEntityInCurrentClient("id", cups.entity)
          };
    }
    return null;
  }
}
